/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CustomerHeader from '../components/CustomerHeader';
import './QuickoPlusPage.css';

const QuickoPlusPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<'annual' | 'monthly'>('annual');

  const benefits = [
    {
      icon: '🚚',
      title: 'Unlimited Free Delivery',
      description: 'Get same-day or next-day delivery on orders of ₹2,905 or more'
    },
    {
      icon: '⛽',
      title: 'Fuel Points',
      description: 'Earn 2 Fuel Points for every ₹83 spent on eligible groceries and general merchandise'
    },
    {
      icon: '💰',
      title: 'Exclusive Savings',
      description: 'Access special discounts and promotions exclusive to Quickko Plus members'
    },
    {
      icon: '📺',
      title: 'Streaming Options',
      description: 'Choose from Disney+ (With Ads), Hulu (With Ads), or ESPN+ with annual membership'
    },
    {
      icon: '⭐',
      title: 'Priority Support',
      description: 'Get priority customer service and faster order processing'
    },
    {
      icon: '🎁',
      title: 'Member-Only Deals',
      description: 'Early access to sales and exclusive member-only promotions'
    }
  ];

  const plans = {
    annual: {
      fastDelivery: {
        price: 8217,
        monthlyPrice: 1078,
        delivery: 'Free delivery in as little as 2 hours',
        streaming: true
      },
      nextDay: {
        price: 5727,
        monthlyPrice: 746,
        delivery: 'Free next-day delivery',
        streaming: false
      }
    },
    monthly: {
      fastDelivery: {
        price: 1078,
        monthlyPrice: 1078,
        delivery: 'Free delivery in as little as 2 hours',
        streaming: false
      },
      nextDay: {
        price: 746,
        monthlyPrice: 746,
        delivery: 'Free next-day delivery',
        streaming: false
      }
    }
  };

  const handleEnroll = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { returnTo: '/quickko-plus' } });
      return;
    }
    // Handle enrollment logic here
    alert('Enrollment functionality will be implemented soon!');
  };

  const handleStartTrial = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { returnTo: '/quickko-plus' } });
      return;
    }
    // Handle free trial logic here
    alert('Free trial functionality will be implemented soon!');
  };

  return (
    <div className="quicko-plus-page">
      {/* Header */}
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        showBackButton
        onBackClick={() => navigate(-1)}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/cart')}
      />

      {/* Hero Section */}
      <section className="quicko-plus-hero">
        <div className="hero-content">
          <h2 className="hero-title">Get More with Quickko Plus</h2>
          <p className="hero-description">
            Join thousands of members enjoying exclusive benefits, free delivery, and amazing savings every day.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <h2 className="section-title">Membership Benefits</h2>
        <div className="benefits-grid">
          {benefits.map((benefit, index) => (
            <div key={index} className="benefit-card">
              <div className="benefit-icon">{benefit.icon}</div>
              <h3 className="benefit-title">{benefit.title}</h3>
              <p className="benefit-description">{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Membership Plans Section */}
      <section className="plans-section">
        <h2 className="section-title">Choose Your Plan</h2>
        <div className="plan-toggle">
          <button
            className={`toggle-btn ${selectedPlan === 'annual' ? 'active' : ''}`}
            onClick={() => setSelectedPlan('annual')}
          >
            Annual
            <span className="toggle-badge">Save More</span>
          </button>
          <button
            className={`toggle-btn ${selectedPlan === 'monthly' ? 'active' : ''}`}
            onClick={() => setSelectedPlan('monthly')}
          >
            Monthly
          </button>
        </div>

        <div className="plans-grid">
          {/* Fast Delivery Plan */}
          <div className="plan-card featured">
            <div className="plan-badge">Most Popular</div>
            <h3 className="plan-name">Fast Delivery</h3>
            <div className="plan-price">
              <span className="price-amount">
                ₹{selectedPlan === 'annual' ? plans.annual.fastDelivery.price.toLocaleString('en-IN') : plans.monthly.fastDelivery.price.toLocaleString('en-IN')}
              </span>
              <span className="price-period">
                {selectedPlan === 'annual' ? '/year' : '/month'}
              </span>
            </div>
            {selectedPlan === 'annual' && (
              <div className="monthly-equivalent">
                Just ₹{plans.annual.fastDelivery.monthlyPrice.toLocaleString('en-IN')}/month
              </div>
            )}
            <ul className="plan-features">
              <li>✓ {plans[selectedPlan].fastDelivery.delivery}</li>
              <li>✓ Unlimited orders</li>
              <li>✓ 2x Fuel Points on all purchases</li>
              {plans[selectedPlan].fastDelivery.streaming && (
                <li>✓ Choose 1 streaming service (Disney+, Hulu, or ESPN+)</li>
              )}
              <li>✓ Exclusive member discounts</li>
              <li>✓ Priority customer support</li>
            </ul>
            <button className="plan-cta-btn primary" onClick={handleEnroll}>
              {selectedPlan === 'annual' ? 'Enroll Now' : 'Start Membership'}
            </button>
          </div>

          {/* Next Day Delivery Plan */}
          <div className="plan-card">
            <h3 className="plan-name">Next Day Delivery</h3>
            <div className="plan-price">
              <span className="price-amount">
                ₹{selectedPlan === 'annual' ? plans.annual.nextDay.price.toLocaleString('en-IN') : plans.monthly.nextDay.price.toLocaleString('en-IN')}
              </span>
              <span className="price-period">
                {selectedPlan === 'annual' ? '/year' : '/month'}
              </span>
            </div>
            {selectedPlan === 'annual' && (
              <div className="monthly-equivalent">
                Just ₹{plans.annual.nextDay.monthlyPrice.toLocaleString('en-IN')}/month
              </div>
            )}
            <ul className="plan-features">
              <li>✓ {plans[selectedPlan].nextDay.delivery}</li>
              <li>✓ Unlimited orders</li>
              <li>✓ 2x Fuel Points on all purchases</li>
              <li>✓ Exclusive member discounts</li>
              <li>✓ Priority customer support</li>
            </ul>
            <button className="plan-cta-btn" onClick={handleEnroll}>
              {selectedPlan === 'annual' ? 'Enroll Now' : 'Start Membership'}
            </button>
          </div>
        </div>
      </section>

      {/* Free Trial Section */}
      <section className="trial-section">
        <div className="trial-content">
          <h2 className="trial-title">Try Quickko Plus Free</h2>
          <p className="trial-description">
            Start your free trial today and experience all the benefits of Quickko Plus membership.
            Cancel anytime during your trial - no charges, no commitment.
          </p>
          <button className="trial-cta-btn" onClick={handleStartTrial}>
            Start Free Trial
          </button>
          <p className="trial-note">
            Free trial available for new members only. Terms and conditions apply.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <h2 className="section-title">Frequently Asked Questions</h2>
        <div className="faq-list">
          <div className="faq-item">
            <h3 className="faq-question">What is Quickko Plus?</h3>
            <p className="faq-answer">
              Quickko Plus is a membership program that offers unlimited free delivery, exclusive savings, 
              fuel points, and more. Choose from annual or monthly plans to fit your needs.
            </p>
          </div>
          <div className="faq-item">
            <h3 className="faq-question">How does free delivery work?</h3>
            <p className="faq-answer">
              With Quickko Plus, you get unlimited free delivery on orders of ₹2,905 or more. 
              Fast Delivery members get same-day or 2-hour delivery, while Next Day members get next-day delivery.
            </p>
          </div>
          <div className="faq-item">
            <h3 className="faq-question">Can I cancel my membership?</h3>
            <p className="faq-answer">
              Yes! You can cancel your Quickko Plus membership at any time. Monthly members can cancel 
              before the next billing cycle, and annual members can cancel anytime with a prorated refund.
            </p>
          </div>
          <div className="faq-item">
            <h3 className="faq-question">What streaming services are included?</h3>
            <p className="faq-answer">
              Annual Fast Delivery members can choose one streaming service: Disney+ (With Ads), 
              Hulu (With Ads), or ESPN+. This benefit is only available with annual plans.
            </p>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="footer-cta">
        <div className="footer-cta-content">
          <h2 className="footer-cta-title">Ready to Get Started?</h2>
          <p className="footer-cta-description">
            Join Quickko Plus today and start saving on every order.
          </p>
          <div className="footer-cta-buttons">
            <button className="footer-cta-btn primary" onClick={handleEnroll}>
              Enroll Now
            </button>
            <button className="footer-cta-btn secondary" onClick={handleStartTrial}>
              Start Free Trial
            </button>
          </div>
          <p className="terms-link">
            By enrolling, you agree to our <a href="#terms">Terms and Conditions</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default QuickoPlusPage;

